import { useEffect } from 'react';

/**
 * Lightweight SEO meta manager (no react-helmet dependency).
 * Sets document.title and head meta/og/twitter tags. Restores them on unmount.
 *
 * @param {Object} opts
 * @param {string} [opts.title]        Page title (becomes "<title> | The Elemental Embrace")
 * @param {string} [opts.description]  Meta description (also used for og/twitter)
 * @param {string} [opts.image]        Absolute or relative URL for og:image / twitter:image
 * @param {string} [opts.url]          Canonical URL (defaults to window.location.href)
 * @param {string} [opts.type]         og:type (default "website")
 * @param {string} [opts.siteName]     og:site_name (default "The Elemental Embrace")
 */
export default function usePageMeta({
  title,
  description,
  image,
  url,
  type = 'website',
  siteName = 'The Elemental Embrace',
} = {}) {
  useEffect(() => {
    const previousTitle = document.title;
    const fullTitle = title
      ? `${title} | ${siteName}`
      : siteName;
    document.title = fullTitle;

    const resolvedUrl =
      url || (typeof window !== 'undefined' ? window.location.href : undefined);

    // Helper: get or create a <meta> tag matching the given attribute selector.
    const ensureMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      let created = false;
      if (!el) {
        el = document.createElement('meta');
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
        created = true;
      }
      return { el, created };
    };

    const ensureLink = (rel) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      let created = false;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
        created = true;
      }
      return { el, created };
    };

    const updates = [];

    const setMeta = (selector, attrs, content) => {
      if (content == null || content === '') return;
      const { el, created } = ensureMeta(selector, attrs);
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      updates.push({ el, prev, created });
    };

    const setLink = (rel, href) => {
      if (!href) return;
      const { el, created } = ensureLink(rel);
      const prev = el.getAttribute('href');
      el.setAttribute('href', href);
      updates.push({ el, prev, created, attr: 'href' });
    };

    // Standard
    setMeta('meta[name="description"]', { name: 'description' }, description);

    // Open Graph
    setMeta('meta[property="og:title"]', { property: 'og:title' }, fullTitle);
    setMeta('meta[property="og:description"]', { property: 'og:description' }, description);
    setMeta('meta[property="og:type"]', { property: 'og:type' }, type);
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, siteName);
    setMeta('meta[property="og:url"]', { property: 'og:url' }, resolvedUrl);
    setMeta('meta[property="og:image"]', { property: 'og:image' }, image);

    // Twitter
    setMeta(
      'meta[name="twitter:card"]',
      { name: 'twitter:card' },
      image ? 'summary_large_image' : 'summary'
    );
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, fullTitle);
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description);
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image);

    // Canonical link
    setLink('canonical', resolvedUrl);

    return () => {
      // Restore document title
      document.title = previousTitle;
      // Restore or remove tags we touched
      updates.forEach(({ el, prev, created, attr }) => {
        const a = attr || 'content';
        if (created) {
          el.remove();
        } else if (prev == null) {
          el.removeAttribute(a);
        } else {
          el.setAttribute(a, prev);
        }
      });
    };
  }, [title, description, image, url, type, siteName]);
}
